import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  ButtonProps as MuiButtonProps,
  Grow,
  Popper,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { map } from 'lodash';
import { transparentize } from 'polished';
import React from 'react';
import { palette as appPalette } from '../../theme';
// import { Button } from '../Button';
import { StyledButtonGroup, StyledButton } from './styles';

/** Largura da faixa clicável da seta (deve coincidir com paddingRight do botão do rótulo). */
const DROPDOWN_TOGGLE_WIDTH_PX = 32;
/** Mesmo fontSize do Typography do rótulo no botão. */
const BUTTON_LABEL_FONT_SIZE_PX = 12;
/** Espaço horizontal extra: padding do LoadingButton + startIcon (quando houver). */
const BUTTON_EXTRA_HORIZONTAL_PX_WITH_ICON = 36;
const BUTTON_EXTRA_HORIZONTAL_PX_NO_ICON = 24;

interface SplitButtonProps extends MuiButtonProps {
  label: string;
  loading?: boolean;
  options: {
    label: string;
    icon?: any;
    disabled?: boolean;
    onClick: (values: any) => void;
  }[];
}

export const SplitButton = (props: SplitButtonProps) => {
  const { label, color = 'primary', loading, options, startIcon, sx } = props;
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const [minGroupWidth, setMinGroupWidth] = React.useState<number | undefined>(
    undefined,
  );

  const optionLabelsKey = options.map((o) => o.label).join('\u0000');

  React.useLayoutEffect(() => {
    const container = measureRef.current;
    if (!container) {
      return;
    }

    const measured = Array.from(
      container.querySelectorAll<HTMLElement>('[data-split-measure]'),
    ).map((el) => el.getBoundingClientRect().width);

    if (measured.length === 0) {
      return;
    }

    const maxTextWidth = Math.max(...measured);
    const extraHorizontal = startIcon
      ? BUTTON_EXTRA_HORIZONTAL_PX_WITH_ICON
      : BUTTON_EXTRA_HORIZONTAL_PX_NO_ICON;

    setMinGroupWidth(
      Math.ceil(maxTextWidth + extraHorizontal + DROPDOWN_TOGGLE_WIDTH_PX),
    );
  }, [label, optionLabelsKey, startIcon, theme.typography.body1]);

  const colorMap = {
    primary: appPalette.primary.main,
    secondary: appPalette.secondary.main,
    success: appPalette.success.main,
    error: appPalette.error.main,
    warning: appPalette.warning.main,
    info: appPalette.text.main,
  };

  const rightButtonBackgroundColor = color in colorMap
    ? colorMap[color as keyof typeof colorMap]
    : undefined;

  const rightButtonDarkOverlay = transparentize(0.85, '#000000');

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  return (
    <>
      <div
        ref={measureRef}
        aria-hidden
        style={{
          position: 'fixed',
          left: -10000,
          top: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <Typography
          data-split-measure
          sx={{ fontSize: `${BUTTON_LABEL_FONT_SIZE_PX}px`, fontWeight: 500 }}
        >
          {label}
        </Typography>
        {map(options, (option) => (
          <Typography
            key={option.label}
            data-split-measure
            sx={{
              fontSize: theme.typography.body1.fontSize,
              fontWeight: theme.typography.body1.fontWeight,
              fontFamily: theme.typography.body1.fontFamily,
            }}
          >
            {option.label}
          </Typography>
        ))}
      </div>
      <StyledButtonGroup
        variant='contained'
        ref={anchorRef}
        aria-label='split button'
        sx={{
          ...sx,
          ...(minGroupWidth !== undefined ? { minWidth: minGroupWidth } : {}),
        }}
      >
        <StyledButton
          variant='contained'
          color={color}
          startIcon={startIcon}
          loading={loading}
          disabled
          sx={{
            flex: 1,
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            justifyContent: 'flex-start',
            paddingRight: `${DROPDOWN_TOGGLE_WIDTH_PX}px`,
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
            }}
            noWrap
          >
            {label}
          </Typography>
        </StyledButton>
        <StyledButton
          variant='contained'
          color={color}
          size='small'
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label='select merge strategy'
          aria-haspopup='menu'
          onClick={handleToggle}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            minWidth: DROPDOWN_TOGGLE_WIDTH_PX,
            width: DROPDOWN_TOGGLE_WIDTH_PX,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderTopRightRadius: '4px',
            borderBottomRightRadius: '4px',
            '& .MuiButton-startIcon': {
              margin: 0,
            },
            ...(rightButtonBackgroundColor
              ? {
                backgroundColor: rightButtonBackgroundColor,
                backgroundImage: `linear-gradient(${rightButtonDarkOverlay}, ${rightButtonDarkOverlay})`,
                '&:hover': {
                  backgroundColor: rightButtonBackgroundColor,
                  backgroundImage: `linear-gradient(${rightButtonDarkOverlay}, ${rightButtonDarkOverlay})`,
                },
                '&.Mui-disabled': {
                  backgroundColor: rightButtonBackgroundColor,
                  backgroundImage: `linear-gradient(${rightButtonDarkOverlay}, ${rightButtonDarkOverlay})`,
                },
              }
              : {}),
          }}
        >
          <ArrowDropDownIcon sx={{ fontSize: 20 }} />
        </StyledButton>
      </StyledButtonGroup>
      <Popper
        style={{
          zIndex: 1000,
          width: anchorRef.current ? anchorRef.current.clientWidth : undefined,
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper sx={{ width: '100%' }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  id='split-button-menu'
                  autoFocusItem
                  sx={{ width: '100%' }}
                >
                  {map(options, (option, index) => (
                    <MenuItem
                      key={option.label}
                      disabled={option.disabled}
                      onClick={option.onClick}
                      sx={{
                        width: '100%',
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}
                    >
                      {option.label} {option.icon}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
